import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { projectService } from '../services/projectService';
import { NewProject } from '../types/project';
import { Client } from '../types/client';
import { crmService } from '../services/crmService';

interface AddProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AddProjectModal: React.FC<AddProjectModalProps> = ({ open, onClose, onSave }) => {
  const [newProject, setNewProject] = useState<Partial<NewProject>>({ status: 'PENDING' });
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchClients = async () => {
        setLoadingClients(true);
        try {
          const clientData = await crmService.getClients();
          setClients(clientData);
        } catch (err) {
          setError('Could not load clients.');
        } finally {
          setLoadingClients(false);
        }
      };
      fetchClients();
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setNewProject((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
        setNewProject((prev) => ({ ...prev, start_date: format(date, 'yyyy-MM-dd') }));
    } else {
        setNewProject((prev) => ({ ...prev, start_date: null }));
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
        setNewProject((prev) => ({ ...prev, end_date: format(date, 'yyyy-MM-dd') }));
    } else {
        setNewProject((prev) => ({ ...prev, end_date: null }));
    }
  };

  const handleSubmit = async () => {
    if (!newProject.name || !newProject.client) {
      setError('Project name and client are required.');
      return;
    } 
    setIsSaving(true);
    setError(null);
    try {
      await projectService.createProject(newProject as NewProject);
      onSave();
      onClose();
      setNewProject({ status: 'PENDING' }); // Reset form
    } catch (err: any) {
      console.error('Failed to create project:', err);
      setError(err.response?.data?.detail || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Project</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField margin="normal" required fullWidth label="Project Name" name="name" onChange={handleChange} autoFocus />
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="client-select-label">Client</InputLabel>
            {loadingClients ? <CircularProgress size={24} /> : (
              <Select
                labelId="client-select-label"
                name="client"
                value={newProject.client || ''}
                label="Client"
                onChange={handleChange}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
          <TextField
            margin="normal"
            fullWidth
            label="Project Value"
            name="project_value"
            type="number"
            onChange={handleChange}
          />
          <TextField margin="normal" fullWidth label="Description" name="description" multiline rows={4} onChange={handleChange} />
          <DatePicker
            label="Start Date"
            value={newProject.start_date ? new Date(newProject.start_date) : null}
            onChange={handleStartDateChange}
            sx={{ width: '100%', mt: 2, mb: 1 }}
          />
          <DatePicker
            label="End Date"
            value={newProject.end_date ? new Date(newProject.end_date) : null}
            onChange={handleEndDateChange}
            sx={{ width: '100%', mt: 2, mb: 1 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              name="status"
              value={newProject.status || 'PENDING'}
              label="Status"
              onChange={handleChange}
            >
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProjectModal;